import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import useSpeakerProfiles from '../hooks/useSpeakerProfiles';
import type { SpeakerInvitationInput, SpeakingEngagement } from '../types';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  engagement: SpeakingEngagement;
  onSubmit: (data: SpeakerInvitationInput) => Promise<void>;
}

export default function InviteDialog({ open, onOpenChange, engagement, onSubmit }: Props) {
  const profiles = useSpeakerProfiles();
  const [speakerId, setSpeakerId] = useState(engagement.speaker_id);
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setSpeakerId(engagement.speaker_id);
      setEmail('');
      setMessage('');
    }
  }, [open, engagement.speaker_id]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!speakerId) return;
    setSubmitting(true);
    try {
      await onSubmit({ speaker_id: speakerId, email: email || null, message: message || null });
      onOpenChange(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Spreker uitnodigen</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="invite-speaker">Spreker</Label>
            <Select value={speakerId || undefined} onValueChange={setSpeakerId}>
              <SelectTrigger id="invite-speaker"><SelectValue placeholder="Kies een spreker" /></SelectTrigger>
              <SelectContent>
                {profiles.map((profile) => (
                  <SelectItem key={profile.id} value={profile.id}>{profile.display_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="invite-email">E-mailadres (optioneel)</Label>
            <Input
              id="invite-email"
              type="email"
              value={email}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
              placeholder="Alleen nodig als de spreker geen account heeft"
            />
          </div>

          <div>
            <Label htmlFor="invite-message">Toelichting (optioneel)</Label>
            <Textarea
              id="invite-message"
              value={message}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setMessage(e.target.value)}
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Annuleren</Button>
            <Button type="submit" disabled={submitting || !speakerId}>Uitnodiging versturen</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
